import React, { useMemo } from "react";
import { useIndicatorSeries } from "../../../hooks/useIndicatorSeries";
import {
  ResponsiveContainer,
  LineChart,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const defaultColors = [
  "#f5b942",
  "#db3736", 
  "#5b7bb0", 
  "#4ab890",
  "#d974b5", 
  "#7a60aa",
  "#52b7d0",
  "#222e3a",
];

function kFmt(v) {
  const n = Number(v) || 0;
  return Math.abs(n) >= 1000 ? `${Math.round(n / 1000)}k` : `${n}`;
}

function groupByYear(arr, reducer, init) {
  const acc = new Map();
  for (const it of arr || []) {
    const y = Number(it.year);
    const val = Number(it.value ?? it.average ?? 0);
    acc.set(y, reducer(acc.get(y) ?? init, val));
  }
  return acc;
}

export default function IndicatorChart({
  indicatorName,
  places,
  selectedPlaces,
  years,
  title,
  colors = defaultColors,
  yTickFormatter,
  tooltipFormatter,
  showHeader = true,
  wrap = true,
  height = 320,
  showLegend = false,
  isPercentage = false,
  useSumApi = false,
}) {
  const isVacanciesCombo = indicatorName === "vacancies_and_rates";

  const fmtY = typeof yTickFormatter === "function" ? yTickFormatter : (v => isPercentage ? `${Number(v).toFixed(1)}%` : `$${Number(v).toFixed(1)}`);
  const fmtTooltip = typeof tooltipFormatter === "function" ? tooltipFormatter : (v => isPercentage ? `${Number(v).toFixed(1)}%` : `$${Number(v).toFixed(1)}`);

  const { data: baseData, loading: loadingBase } = useIndicatorSeries(
    isVacanciesCombo ? "vacancies_count" : indicatorName,
    places,
    years,
    useSumApi ? "sum" : "average"
  );

  const { data: rateData, loading: loadingRate } = useIndicatorSeries(
    isVacanciesCombo ? "vacancy_rate_pct" : null,
    places,
    years
  );

  const loading = isVacanciesCombo ? (loadingBase || loadingRate) : loadingBase;

  const filteredPlaces = useMemo(
    () => (selectedPlaces && selectedPlaces.length ? selectedPlaces : places) || [],
    [selectedPlaces, places]
  );

  const colorMap = useMemo(() => {
    const map = {};
    (places || []).forEach((place, idx) => (map[place] = colors[idx % colors.length]));
    return map;
  }, [places, colors]);

  const chartSeries = useMemo(() => {
    const out = [];

    for (const place of filteredPlaces) {
      const series = baseData?.[place] || [];
      const summed = groupByYear(series, (a, b) => a + b, 0);
      for (const [year, total] of summed.entries()) {
        let row = out.find(r => r.year === year);
        if (!row) { row = { year }; out.push(row); }
        row[place] = total;
      }
    }

    if (isVacanciesCombo && rateData) {
      const perYearAverages = new Map();
      for (const place of filteredPlaces) {
        const series = rateData?.[place] || [];
        const sum = groupByYear(series, (a, b) => a + b, 0);
        const count = groupByYear(series, (a) => (a + 1), 0);
        for (const [y, s] of sum.entries()) {
          const c = count.get(y) || 1;
          const avg = s / c;
          (perYearAverages.get(y) || perYearAverages.set(y, []).get(y)).push(avg);
        }
      }
      for (const row of out) {
        const arr = perYearAverages.get(row.year) || [];
        if (arr.length) row.__vacancy_rate_avg = arr.reduce((a, b) => a + b, 0) / arr.length;
      }
    }

    return out.sort((a, b) => a.year - b.year);
  }, [baseData, rateData, filteredPlaces, isVacanciesCombo]);

  const noData = !loading && (!chartSeries.length || filteredPlaces.every(p => chartSeries.every(r => r[p] == null)));

  const maybeLegend = showLegend ? (
    <Legend verticalAlign="top" align="left" height={52} />
  ) : null;

  const ChartBlock = () => {
    if (loading) return <div className="chart-placeholder">Loading chart...</div>;
    if (noData) return <div className="chart-placeholder">No data available for the current selection.</div>;

    if (indicatorName === "rental_yield_pct") {
      return (
        <ResponsiveContainer width="100%" height={height}>
                  <LineChart data={chartSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={typeof yTickFormatter === "function" ? yTickFormatter : (v => `${Number(v)}%`)} />
                    <Tooltip formatter={typeof tooltipFormatter === "function" ? tooltipFormatter : (v => `${Number(v)}%`)} />
                    {maybeLegend}
                    {filteredPlaces.map((place) => (
                      <Line
                        key={place}
                        type="monotone"
                        dataKey={place}
                        stroke={colorMap[place]}
                        dot={false}
                        activeDot={{ r: 5 }}
                        strokeWidth={2}
                      />
                    ))}
                  </LineChart>
        </ResponsiveContainer>
      );
    }

    if (indicatorName === "vacancy_rate_pct") {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartSeries}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis tickFormatter={typeof yTickFormatter === "function" ? yTickFormatter : (v => `${Number(v)}%`)} />
            <Tooltip formatter={typeof tooltipFormatter === "function" ? tooltipFormatter : (v => `${Number(v)}%`)} />
            {maybeLegend}
            {filteredPlaces.map((place) => (
              <Line
                key={place}
                type="monotone"
                dataKey={place}
                stroke={colorMap[place]}
                dot={false}
                activeDot={{ r: 5 }}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (isVacanciesCombo) {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={chartSeries}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis yAxisId="left" tickFormatter={typeof yTickFormatter === "function" ? yTickFormatter : kFmt} />
            <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${Number(v).toFixed(0)}%`} />
            <Tooltip
              formatter={(v, name) =>
                name === "Vacancy rate" ? [`${Number(v).toFixed(1)}%`, name] : [fmtTooltip(v), name]
              }
            />
            {maybeLegend}
            {filteredPlaces.map((place) => (
              <Bar key={place} yAxisId="left" dataKey={place} name={place} fill={colorMap[place]} stackId="a" radius={[4,4,0,0]} barSize={28} />
            ))}
            <Line yAxisId="right" type="monotone" dataKey="__vacancy_rate_avg" name="Vacancy rate" stroke="#222e3a" dot={false} strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartSeries}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis tickFormatter={fmtY} />
          <Tooltip formatter={fmtTooltip} />
          {maybeLegend}
          {filteredPlaces.map((place) => (
            <Line key={place} type="monotone" dataKey={place} stroke={colorMap[place]} dot={false} activeDot={{ r: 5 }} strokeWidth={2} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  if (!wrap) return <ChartBlock />;

  return (
    <div className="indicator-card">
      {showHeader && (
        <div className="chart-title">
          {title}
          <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>
      )}
      <div className="card-body">
        <ChartBlock />
      </div>
    </div>
  );
}
